/**
 * HYPSEYA — Privacy Policy
 * GDPR-compliant privacy policy page
 */
import { useNavigate } from 'react-router-dom';

const SECTIONS = [
  {
    id: 'who-we-are',
    title: 'Who we are',
    content: [
      'HYPSEYA is an emotional regulation platform developed by HYPSEYA Lda.',
      'We are the data controller for all personal data collected through the HYPSEYA app and website. This policy complies with the EU General Data Protection Regulation (GDPR — Regulamento (UE) 2016/679).',
      'Contact: privacy@hypseya.com',
    ],
  },
  {
    id: 'data-collected',
    title: 'What data we collect',
    content: [
      'Account information — your name and email address, authenticated via Auth0. We never store your password directly.',
      'Practice data — Daily Loop completions, ERI scores, journal entries, somatic check-ins, and session timestamps. This is the core of your personal record.',
      'Device information — used only for push notifications, and only if you grant permission.',
      'No financial data is stored on our servers. Payments are processed by Stripe, which has its own privacy policy.',
    ],
  },
  {
    id: 'how-we-use',
    title: 'How we use your data',
    content: [
      'To generate your personalised ERI (Emotional Regulation Index) and practice insights.',
      'To deliver the Daily Loop content, AI-generated meditations, and program recommendations.',
      'To send optional push notifications — only with your explicit permission, and you can withdraw at any time.',
      'We never use your data for advertising. We never profile you for commercial purposes.',
    ],
  },
  {
    id: 'who-sees',
    title: 'Who sees your data',
    content: [
      'Only you, unless you explicitly generate and share a therapist report link.',
      'Anonymised and aggregated data may be used to improve the ERI algorithm and the collective intelligence layer (Integration Circles). No individual is identifiable from this data.',
      'We never sell personal data. We never share it with third parties for marketing.',
    ],
  },
  {
    id: 'storage-security',
    title: 'Data storage & security',
    content: [
      'Data is stored on servers within the EU (Railway infrastructure / MongoDB Atlas). All data is encrypted in transit (HTTPS) and at rest.',
      'Authentication is handled by Auth0 — a SOC 2 Type II certified identity provider. We never store passwords.',
      'We retain your data for as long as your account is active. You can request deletion at any time.',
    ],
  },
  {
    id: 'your-rights',
    title: 'Your rights (GDPR)',
    content: [
      'Right to access — you can request a full copy of your data at any time.',
      'Right to correction — you can update or correct any personal information.',
      'Right to deletion — you can request that we delete your account and all associated data.',
      'Right to data portability — you can request your data in a machine-readable format.',
      'Right to withdraw consent — you can withdraw any consent you have given at any time, without affecting the lawfulness of prior processing.',
      'To exercise any of these rights, contact us at privacy@hypseya.com.',
    ],
  },
  {
    id: 'cookies',
    title: 'Cookies',
    content: [
      'We use minimal cookies — primarily for authentication (Auth0 session cookies) and anonymised analytics (PostHog).',
      'We do not use advertising cookies. We do not use third-party tracking cookies.',
      'You can manage cookie preferences through your browser settings at any time.',
    ],
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    content: [
      'We will notify you in-app of any material changes to this privacy policy, with at least 14 days notice before changes take effect.',
      'The version date at the top of this page reflects when the policy was last updated.',
    ],
  },
];

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 55%, rgba(184,211,207,0.15) 100%)',
      paddingBottom: '100px',
    }}>
      {/* Back button */}
      <div style={{ padding: '20px 20px 0', maxWidth: '600px', margin: '0 auto' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px',
            color: '#1E6B68', fontSize: '0.85rem', fontWeight: '500',
            padding: '8px 0', letterSpacing: '0.02em',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M11 4L6 9l5 5" stroke="#1E6B68" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
      </div>

      {/* Header */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '24px 20px 32px' }}>
        <p style={{
          margin: '0 0 8px',
          fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.12em',
          color: '#8FA3A0', textTransform: 'uppercase',
        }}>
          Legal
        </p>
        <h1 style={{
          margin: '0 0 10px',
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.9rem', fontWeight: '400',
          color: '#1E3A38', lineHeight: 1.2,
        }}>
          Privacy Policy
        </h1>
        <p style={{ margin: 0, fontSize: '0.82rem', color: '#9AADAB' }}>
          Last updated: May 2026
        </p>
      </div>

      {/* Sections */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 20px' }}>
        {SECTIONS.map((section, idx) => (
          <div
            key={section.id}
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '20px 22px',
              marginBottom: '10px',
              boxShadow: '0 2px 10px rgba(30,107,104,0.05)',
            }}
          >
            <h2 style={{
              margin: '0 0 14px',
              fontSize: '0.95rem', fontWeight: '700',
              color: '#1E6B68', letterSpacing: '0.01em',
            }}>
              {idx + 1}. {section.title}
            </h2>
            {section.content.map((paragraph, i) => (
              <p
                key={i}
                style={{
                  margin: i < section.content.length - 1 ? '0 0 10px' : '0',
                  fontSize: '0.87rem', color: '#555',
                  lineHeight: 1.7,
                }}
              >
                {paragraph}
              </p>
            ))}
          </div>
        ))}

        {/* Contact footer */}
        <div style={{
          marginTop: '20px', padding: '18px 22px',
          background: 'rgba(30,107,104,0.06)',
          borderRadius: '14px',
          textAlign: 'center',
        }}>
          <p style={{ margin: '0 0 4px', fontSize: '0.82rem', color: '#7A9E9B' }}>
            Questions about this policy?
          </p>
          <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '600', color: '#1E6B68' }}>
            privacy@hypseya.com
          </p>
        </div>
      </div>
    </div>
  );
}
